import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn } from '@/hooks/useAuth'
import {X, Loader2, Eye, EyeOff, ShieldCheck} from 'lucide-react'
import toast from 'react-hot-toast'

interface AdminLoginModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function AdminLoginModal({ isOpen, onClose }: AdminLoginModalProps) {
  const [username, setUsername]         = useState('')
  const [password, setPassword]         = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading]           = useState(false)

  const usernameRef = useRef<HTMLInputElement>(null)
  const navigate    = useNavigate()

  // Reset ao abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => usernameRef.current?.focus(), 80)
      setUsername('')
      setPassword('')
      setShowPassword(false)
    }
  }, [isOpen])

  // Fechar com Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [isOpen, onClose])

  // Bloquear scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim()) { toast.error('Digite o nome de utilizador.'); return }
    if (!password)        { toast.error('Digite a senha.'); return }
    setLoading(true)
    try {
      await signIn(username, password)
      toast.success('Login realizado com sucesso!')
      onClose()
      navigate('/admin')
    } catch (err: any) {
      toast.error(err?.message ?? 'Utilizador ou senha incorretos.')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Login administrativo"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div className="relative w-full max-w-sm animate-fade-in">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="h-1 w-full bg-blue-600" />
          <div className="p-6">

            {/* Cabeçalho */}
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                    Acesso Administrativo
                  </h2>
                  <p className="text-xs text-gray-400">Palácio da Sabedoria</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                aria-label="Fechar modal"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form de login */}
            <form onSubmit={handleLogin} className="space-y-4" noValidate>
              <div>
                <label htmlFor="modal-username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Utilizador
                </label>
                <input
                  ref={usernameRef}
                  id="modal-username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Ex: Admin"
                  required
                  autoComplete="username"
                  spellCheck={false}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all placeholder-gray-400"
                />
              </div>
              <div>
                <label htmlFor="modal-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <input
                    id="modal-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    className="w-full px-3.5 py-2.5 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-red-400 text-white font-semibold rounded-lg text-sm transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                {loading
                  ? <><Loader2 size={15} className="animate-spin" />A entrar...</>
                  : 'Entrar no Painel'
                }
              </button>
            </form>

            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-400 text-center">
                Área restrita — apenas administradores
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
